import { type CardFields, cardFields } from "@mn/shared";
import RBush from "rbush";
import * as Y from "yjs";
import type { Camera, Rect } from "./camera";
import { screenToPage } from "./camera";

export interface Item {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
  id: string;
}

/** Spatial index of free cards kept in sync with the doc, outside React. Heights of content-sized cards are measured by the DOM. */
export class SpatialIndex {
  private tree = new RBush<Item>();
  private items = new Map<string, Item>();
  private heights = new Map<string, number>();
  private listeners = new Set<() => void>();
  version = 0;

  constructor(private cards: Y.Map<Y.Map<unknown>>) {
    cards.forEach((c, id) => this.upsert(id, cardFields(c)));
    cards.observeDeep((events) => {
      let changed = false;
      for (const e of events) {
        if (e.target === cards) {
          for (const [id, ch] of (e as Y.YMapEvent<Y.Map<unknown>>).changes.keys) {
            if (ch.action === "delete") this.remove(id);
            else {
              const c = cards.get(id);
              if (c) this.upsert(id, cardFields(c));
            }
            changed = true;
          }
        } else if (e.target instanceof Y.Map && e.target.parent === cards) {
          const keys = (e as Y.YMapEvent<unknown>).keysChanged;
          if (["x", "y", "w", "h", "parent", "unsorted"].some((k) => keys.has(k))) {
            const id = e.target.get("id") as string;
            this.upsert(id, cardFields(e.target));
            changed = true;
          }
        }
      }
      if (changed) this.bump();
    });
  }

  private bump() {
    this.version++;
    for (const l of this.listeners) l();
  }
  subscribe = (l: () => void) => {
    this.listeners.add(l);
    return () => {
      this.listeners.delete(l);
    };
  };

  setHeight(id: string, h: number) {
    if (this.heights.get(id) === h) return;
    this.heights.set(id, h);
    const c = this.cards.get(id);
    if (c) {
      this.upsert(id, cardFields(c));
      this.bump();
    }
  }

  rectOf(id: string): Rect | null {
    const it = this.items.get(id);
    return it ? { x: it.minX, y: it.minY, w: it.maxX - it.minX, h: it.maxY - it.minY } : null;
  }

  private upsert(id: string, c: CardFields) {
    this.remove(id);
    if (c.parent || (c as unknown as { unsorted?: boolean }).unsorted) return; // columns position their children; unsorted cards live in the panel
    const h = c.h ?? this.heights.get(id) ?? 80;
    const it: Item = { minX: c.x, minY: c.y, maxX: c.x + c.w, maxY: c.y + h, id };
    this.items.set(id, it);
    this.tree.insert(it);
  }
  private remove(id: string) {
    const prev = this.items.get(id);
    if (prev) {
      this.tree.remove(prev, (a, b) => a.id === b.id);
      this.items.delete(id);
    }
  }

  visible(c: Camera, vw: number, vh: number, pad = 400): Set<string> {
    const tl = screenToPage(c, -pad, -pad);
    const br = screenToPage(c, vw + pad, vh + pad);
    return new Set(this.tree.search({ minX: tl.x, minY: tl.y, maxX: br.x, maxY: br.y }).map((i) => i.id));
  }
  inRect(r: Rect): string[] {
    return this.tree.search({ minX: r.x, minY: r.y, maxX: r.x + r.w, maxY: r.y + r.h }).map((i) => i.id);
  }
  contentBounds(): Rect | null {
    if (this.items.size === 0) return null;
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    for (const it of this.items.values()) {
      minX = Math.min(minX, it.minX);
      minY = Math.min(minY, it.minY);
      maxX = Math.max(maxX, it.maxX);
      maxY = Math.max(maxY, it.maxY);
    }
    return { x: minX, y: minY, w: maxX - minX, h: maxY - minY };
  }
}
